<style lang="scss">
  @import '~foundation-sites/scss/util/util';

  .investment-mix {
    position: relative;

    h4 {
      font-size: 1.1em;
    }

    .button-group {
      height: 90px;

      a.button {
        display: block;
        float: left;
        position: relative;

        height: 70px;
        width: calc(20% - 5px) !important;
        margin-left: 5px;
        &:first-child {
          margin-left: 0;
        }

        color: #3C3C3B;

        span {
          width: 100%;
          text-align: center !important;
        }

        &.active:after {
          border-top-color: #009EE2;
        }

        &:after {
          top: 100%;
          left: 50%;
          border: solid transparent;
          content: " ";
          height: 0;
          width: 0;
          position: absolute;
          pointer-events: none;
          border-color: rgba(0, 0, 0, 0);
          transition: 125ms border-top-color;
          border-width: 15px;
          margin-left: -15px;
        }
      }
    }

    .route {
      background-color: #ECECEC;
      height: 10px;
      width: calc(80.5%);
      margin: 15px auto;
      position: relative;

      .point {
        height: 26px;
        width: 26px;
        border-radius: 50%;
        position: absolute;
        cursor: pointer;

        float: left;
        margin-top: -8px;
        transition: 250ms background-color;

        background-color: #ECECEC;

        &:hover {
          background-color: #E1E87F;
        }

        &.active {
          background-color: #BDC63F;
        }

        &:nth-child(1) {
          left: calc(0% - 13px);
        }

        &:nth-child(2) {
          left: calc(25% - 13px);
        }

        &:nth-child(3) {
          left: calc(50% - 13px);
        }

        &:nth-child(4) {
          left: calc(75% - 13px);
        }

        &:nth-child(5) {
          left: calc(100% - 13px);
        }
      }

      .defensive {
        left: -15px;
      }

      .growth {
        right: -15px;
      }

      .defensive, .growth {
        position: absolute;
        margin-top: 20px;

        font-size: 0.75em;
      }
    }

    >div.description {
      background: #B8E3F7;
      padding: 25px;
      border-radius: 10px;
      margin: 30px auto;
      width: 90%;

      .bold { font-weight: bold; }

      p {
        margin-bottom: 0;
      }

      .help-icon {
        position: absolute;
        top: -5px;
        right: -5px;

        .icon-background-fill { fill: #2199e8; }
        &:hover .icon-background-fill { fill: #1f82c3; }
      }
    }

    @media screen and (max-width: 440px){
      .route { display: none; }
      .button-group {
        height: auto !important;

        a.button {
          float: none;
          display: block;
          width: 100% !important;
          margin: 5px 0 0 0;
          margin-right: 0 !important;

          &:after {
            display: none;
          }
        }
      }
      & > div.description {
        margin: 10px 0;
        width: 100%;
      }
    }
  }
</style>

<template>
  <div class="investment-mix">
    <slot></slot>
    <checkbox name="investment-type" :items="visibleOptions" :selected="selected" @change="onChange($event.value)" :expanded="true"></checkbox>
    <div class="route">
      <div class="point" :class="{ active: option.value == selected.value }" @click="onChange(option.value)" v-for="option in visibleOptions" :key="option.value"></div>
      <div class="defensive">Defensive</div>
      <div class="growth">Growth</div>
    </div>
    <div class="description">
      <div class="relative">
        <help-icon message="The before retirement Custom Investment Mix expected return is less taxes" v-if="selectedCustomOption && !inRetirement" />
        <span class="bold">{{ selectedOption.text }} {{ selectedCustomOption ? 'Investment mix return' : 'Risk' }}:</span>
        <p>{{ description }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import Vue from 'vue';
import { mapState } from 'vuex';
// import { calculateClosestTaxableIncome } from '@/store/helpers';

export default {
  props: {
    selected: {
      required: true,
    },
    inRetirement: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState(['inferred']),
    selectedOption() {
      const selected = this.selected.value;
      return _.find(this.investmentOptions, o => o.value === selected);
    },
    selectedCustomOption() {
      return this.selectedOption.text === 'Custom';
    },
    description() {
      return this.inRetirement ? this.selectedOption.inRetirementDescription : this.selectedOption.description;
    },
    visibleOptions() {
      return _.filter(this.investmentOptions, (option, key) => key !== 'custom');
    },
    investmentOptions() {
      const customGross = this.inferred.GrossReturn.value;
      const customTaxable = this.inferred.TaxableFraction.value;
      const workingGross = customGross * (1 - (customTaxable * 0.15));
      const filter = Vue.filter('percentageDisplay');
      // console.log('customTaxable',customTaxable);
      // console.log('workingGross',workingGross);
      // console.log('investment options', calculateClosestTaxableIncome(0.06));

      return {
        low: {
          value: 1,
          text: 'Low risk',
          description: 'Expected return of 4.0% p.a.',
          inRetirementDescription: 'Expected return of 4.5% p.a.',
        },
        lowMedium: {
          value: 2,
          text: 'Medium / Low risk',
          description: 'Expected return of 5.0% p.a.',
          inRetirementDescription: 'Expected return of 5.5% p.a.',
        },
        mediumHigh: {
          value: 3,
          text: 'Medium risk',
          description: 'Expected return of 5.5% p.a.',
          inRetirementDescription: 'Expected return of 6.0% p.a.',
        },
        high: {
          value: 4,
          text: 'Medium / High risk',
          description: 'Expected return of 6.0% p.a.',
          inRetirementDescription: 'Expected return of 6.5% p.a.',
        },
        highest: {
          value: 5,
          text: 'High risk',
          description: 'Expected return of 6.5% p.a.',
          inRetirementDescription: 'Expected return of 7.0% p.a.',
        },
        custom: {
          value: 6,
          text: 'Custom',
          description: `Expected return of ${filter(workingGross)} p.a.`,
          inRetirementDescription: `Expected return of ${filter(customGross)} p.a.`,
        },
      };
    },
  },
  methods: {
    onChange(value) {
      this.$emit('change', { value });
    },
  },
};
</script>
